import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Fingerprint, ShieldCheck } from 'lucide-react';
import { useBiometric } from '../hooks/useBiometric';

/**
 * BiometricGate - full-screen overlay shown on TWA when the user has
 * enabled biometric unlock and the app is currently locked. Renders
 * children once the prompt succeeds. Pure passthrough in the browser
 * (where the bridge is unavailable) so web users are never blocked.
 */
const BiometricGate = ({ children }) => {
    const { isSupported, enabled, locked, unlock } = useBiometric();

    // Auto-prompt as soon as we know we're locked so the user doesn't have
    // to tap "Unlock" first - the system prompt appears immediately.
    useEffect(() => {
        if (isSupported && enabled && locked) {
            unlock();
        }
    }, [isSupported, enabled, locked, unlock]);

    if (!isSupported || !enabled || !locked) {
        return children;
    }

    return (
        <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-8"
            style={{
                background:
                    'radial-gradient(ellipse 80% 60% at 50% 0%, #DCFCE7 0%, transparent 60%), #FDFCF8',
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            data-testid="biometric-gate"
        >
            <motion.div
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-24 h-24 rounded-3xl bg-[#2E3A2F] flex items-center justify-center shadow-[0_20px_60px_-15px_rgba(46,58,47,0.5)] mb-8"
            >
                <Fingerprint className="w-12 h-12 text-emerald-300" strokeWidth={1.5} />
            </motion.div>
            <h1 className="text-[#2E3A2F] font-heading font-bold text-2xl mb-2 text-center">
                BrainSync is locked
            </h1>
            <p className="text-gray-600 text-center text-[15px] mb-10 max-w-[280px]">
                Use your fingerprint or face to unlock
            </p>
            <button
                onClick={unlock}
                data-testid="biometric-unlock-btn"
                className="bg-[#2E3A2F] text-white rounded-full h-12 px-7 flex items-center justify-center gap-2 font-semibold text-[14px] shadow-[0_10px_24px_-8px_rgba(46,58,47,0.5)] active:scale-[0.98] transition-transform"
            >
                <ShieldCheck className="w-4 h-4" />
                Unlock
            </button>
        </div>
    );
};

export default BiometricGate;
